import { DateTime } from "luxon";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { Breadcrumb } from "../components/Breadcrumbs";
import { Flex } from "../components/Flex";
import { H3 } from "../components/Heading";
import { Loader } from "../components/Loader";
import { Paragraph } from "../components/Paragraph";
import Topbar from "../components/Topbar";
import { ROUTE } from "../routes";
import { buildName, capitalise } from "../utils/string";
import { Image } from "../components/Image";
import { Box } from "../components/Box";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { checksStatusName } from "./IndividualView/helper";
import axios from "axios";
import theme from "../theme";
import { showError } from "../utils/error-handling";
import { notify, clearAll } from "../utils/notify";
import { useAuth } from "../context/AuthContext";
import downloadIcon from "../assets/download-arrow.svg";
import { Modal } from "../components/Modal";
import { FaSortDown, FaSortUp, FaTrash } from "react-icons/fa";
import { IconButtonDropdown } from "../components/IconButtonDropdown";
import { IconButtonDropdownOption } from "../components/IconButtonDropdown/IconButtonDropdownOption";
import { env } from "../utils/env";
import resendInviteIcon from "../assets/resend-invite-icon.svg";
import { useMandate } from "../data/useMandate";
import { useMonitoringDetail } from "../data/useMonitoringDetail";
import { useMonitoringActivityData } from "../data/useMonitoringActivityData";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { Icon } from "../components/Icon";
import success from "../assets/card-success-icon.svg";
import inReview from "../assets/card-in-review-icon.svg";
import pending from "../assets/pending-icon.svg";
import { AdverseMediaMonitoring } from "./IndividualView/AdverseMediaMonitoring";
import { PepAndSanctionsMonitoring } from "./IndividualView/PepAndSanctionsMonitoring";
import { queryClient } from "../queryClient";
import manuallyRejected from "../assets/manually_rejected.svg";
import manuallyAccepted from "../assets/manually_accepted.svg";
import { useDeleteMonitoringRecord } from "../data/useDeleteMonitoringRecord";
import { useCountries } from "../data/useCountries";
import { useEntityTypes } from "../data/useEntityTypes";
import { ProfileRecordWidget } from "../components/ProfileRecordWidget";
import { RecordSharing } from "../components/RecordAccess";
import {
  MonitoringRecordSummary,
  MonitoringScreeningBody,
} from "./MonitoringScreening";
import { TimelineSelection } from "./MonitoringScreening/useMonitoringScreeningModel";
import {
  isLn2Provider,
  SCREENING_EVENT,
} from "../utils/monitoringScreening";

const Heading = styled(H3)`
  ${({ theme: { fontSizes } }) => `
    font-size: ${fontSizes[2]};
    word-break: break-all;
  `}
`;

const Container = styled.div`
  ${({ theme: { space } }) => `
    padding: ${space[4]};
    height: calc(100vh - 215px);
    overflow-y: auto;
    padding-bottom: ${space[5]}
  `}
`;

const CardLine = ({ children, ...styleProps }: any) => (
  <Paragraph fontSize={1} color="gray.60" my={0} {...styleProps}>
    {children}
  </Paragraph>
);

export const IndividualMonitoringView = (props: any) => {
  const params = useParams();
  const { authHeader, hasPermission } = useAuth();
  const navigate = useNavigate();

  const [isRequestingPdfDownload, setIsRequestingPdfDownload] = useState(false);
  const [checkContent] = useState(null);
  const [monitoringId, setMonitoringId] = useState(null);
  const [isPepAndSanctionsCheck, setIsPepAndSanctionsCheck] = useState(false);
  const [isAdverseMediaCheck, setIsAdverseMediaCheck] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  // LexisNexis2 only. null means the timeline's own default, the newest alert
  const [timelineSelection, setTimelineSelection] =
    useState<TimelineSelection | null>(null);
  const [sorting, setSorting] = useState<SortingState>([
    { id: "created_on", desc: true },
  ]);
  const { data: countries } = useCountries();
  const { data: entityTypes } = useEntityTypes();

  // Switch on the activity's `event`, not its title. The previous branch covered
  // only two of the five values, so Screening, Company Sanctions and Company
  // Adverse Media rows had a View button that did nothing at all.
  const handleViewClick = (event: string, id: number) => {
    switch (event) {
      case "PEP & Sanctions":
      case "Sanctions":
      case "Company Sanctions":
        setMonitoringId(id);
        setIsPepAndSanctionsCheck(true);
        break;
      case "Adverse Media":
      case "Company Adverse Media":
        setMonitoringId(id);
        setIsAdverseMediaCheck(true);
        break;
      case "Screening":
        // a LexisNexis2 alert is not a slider: it scopes the page, so this
        // selects it on the timeline rail in place
        setTimelineSelection(id);
        break;
      default:
        break;
    }
  };
  const { data: monitoringData, isLoading } = useMonitoringDetail(params.id);

  const individual = monitoringData?.data;

  const isIndividual = individual?._type === "individual";
  const isEntity = individual?._type === "entity";

  const individualName = isLoading
    ? "..."
    : isIndividual
    ? buildName([
        individual?.first_name,
        individual?.middle_name,
        individual?.last_name,
      ])
    : individual?.company_name;

  const { data: monitoringActivityData } = useMonitoringActivityData(params.id);

  // Map country ID to country object (with icon)
  const country = countries?.data.find((c) => c.id === individual?.country);

  const countryLabel = country?.name;
  const countryFlag = country?.icon;

  const entityType =
    individual?.entity_type && entityTypes?.data
      ? entityTypes.data.find((e) => e.id === individual.entity_type)
      : null;

  const entityTypeName = entityType?.name ?? "-";

  const monitoringActivity = monitoringActivityData?.data;

  // A record renders according to the check it actually ran, so this branches on
  // the record's own provider_reference. Deliberately not useScreeningProvider,
  // which reads tenant billing and is a creation-time switch.
  const isLn2 = isLn2Provider(individual?.provider_reference);

  const screeningActivities = (monitoringActivity ?? []).filter(
    (activity: any) => activity?.event === SCREENING_EVENT,
  );
  const legacyActivities = (monitoringActivity ?? []).filter(
    (activity: any) => activity?.event !== SCREENING_EVENT,
  );

  // Structure by what the record actually holds rather than by provider alone,
  // so a mixed history and a rollback to the legacy provider both fall out
  // without a special case. The two About cards are wrong on every LexisNexis2
  // record: both booleans are written true on submit, but no LexisNexis2
  // activity ever carries those event names, so they read "Required Yes" with
  // both dates blank.
  const showAboutCards = legacyActivities.length > 0;
  const showScreeningView = isLn2 || screeningActivities.length > 0;

  let firstActivity, firstActivityAdverse, lastActivity, lastActivityAdverse;
  let pepArray = [];
  let adverseArray = [];

  monitoringActivity?.forEach(function (e) {
    if (
      e.event === "PEP & Sanctions" ||
      e.event === "Sanctions" ||
      e.event === "Company Sanctions"
    ) {
      pepArray.push(e);
      let length = pepArray.length;
      pepArray.sort(
        (a, b) => Date.parse(b.created_on) - Date.parse(a.created_on),
      );
      lastActivity = pepArray[0].created_on;
      firstActivity = pepArray[length - 1].created_on;
    }
    if (e.event === "Adverse Media" || e.event === "Company Adverse Media") {
      adverseArray.push(e);
      let length = adverseArray.length;
      adverseArray.sort(
        (a, b) => Date.parse(b.created_on) - Date.parse(a.created_on),
      );
      lastActivityAdverse = adverseArray[0].created_on;
      firstActivityAdverse = adverseArray[length - 1].created_on;
    }
  });

  const monitoringIcons = {
    accepted: success,
    review: inReview,
    pending: pending,
    manually_accepted: manuallyAccepted,
    manually_rejected: manuallyRejected,
  };

  const cols = [
    {
      header: () => "Event",
      accessorKey: "event",
      enableSorting: true,
      cell: (info) => {
        const { event } = info.row.original;
        return (
          <span
            style={{
              fontWeight: "bold",
              fontSize: "18px",
              color: theme.colors.darkBlue,
            }}
          >
            {event}
          </span>
        );
      },
    },
    {
      header: () => "Status",
      accessorKey: "status",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.status?.status || "";
        const b = rowB.original.status?.status || "";
        return a.localeCompare(b);
      },
      cell: (info) => {
        const status = info.getValue();
        // the activity status is nullable in the backend, and an activity
        // without one must not take the whole page down
        if (!status) return <Flex color={theme.colors.gray[40]}>-</Flex>;
        return (
          <Flex justifyContent="start" alignItems="center">
            <span key={status.id} style={{ margin: "0 5px" }}>
              <Image
                src={monitoringIcons[status.status]}
                alt="checks"
                height="auto"
                width="22px"
                mr={1}
              />
              {checksStatusName[status.status]}
            </span>
          </Flex>
        );
      },
    },
    {
      header: () => "Creation Date",
      accessorKey: "created_on",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.created_on
          ? new Date(rowA.original.created_on).getTime()
          : 0;
        const b = rowB.original.created_on
          ? new Date(rowB.original.created_on).getTime()
          : 0;
        return a - b;
      },
      cell: (info) => {
        return (
          <Flex color={theme.colors.gray[40]}>
            {DateTime.fromISO(info.getValue()).toFormat("dd/MM/yyyy")}
          </Flex>
        );
      },
    },
    {
      header: () => "",
      id: "view_id",
      accessorKey: "id",
      enableSorting: false,
      cell: (info) => {
        const { id } = info.row.original;
        const { event } = info.row.original;
        if (info.row.original.status?.status !== "pending") {
          return (
            <Button
              size="regular"
              variant="text"
              fontWeight="bold"
              onClick={() => handleViewClick(event, id)}
            >
              View
            </Button>
          );
        } else {
          return null;
        }
      },
    },
  ];

  const [columns] = useState([...cols]);

const table = useReactTable({
  data: monitoringActivity ?? [],
  columns,
  state: {
    sorting,
  },
  onSortingChange: setSorting,
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
});

  const { data: mandateData } = useMandate(individual?.mandate);
  const mandate = mandateData?.data;

  const handleDownloadPdfClick = () => {
    if (isRequestingPdfDownload) {
      return;
    }

    setIsRequestingPdfDownload(true);
    notify("Generating PDF, this could take up to 30 seconds", {
      position: "bottom-left",
      type: "info",
      pauseOnFocusLoss: false,
      pauseOnHover: false,
      autoClose: false,
    });

    axios
      .get(
        `${env.verifyServiceUrl}individual/download_monitoring_pdf/${params.id}`,
        {
          headers: {
            Authorization: authHeader,
          },
          responseType: "arraybuffer",
        },
      )
      .then((response) => {
        clearAll();
        const blob = new Blob([response.data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);

        const timestamp = DateTime.now().toFormat("yyyy-MM-dd_HH-mm-ss");

        const name = isIndividual
          ? buildName([
              individual?.first_name,
              individual?.middle_name,
              individual?.last_name,
            ])
          : individual?.company_name || "-";

        link.download = `Customer Report - ${individual?.reference} - ${name} - ${timestamp}.pdf`;

        link.click();
      })
      .catch(() => {
        clearAll();
        setTimeout(() => {
          notify("There was a problem while generating the PDF report", {
            position: "bottom-left",
            type: "error",
          });
        }, 100);
      })
      .finally(() => {
        setIsRequestingPdfDownload(false);
      });
  };

  const refetchIndividual = () => {
    queryClient.invalidateQueries(["individual", params.id]);
  };
  const { mutate: deleteMonitoring } = useDeleteMonitoringRecord();
  const handleConfirmDeleteMonitoringRecord = () => {
    setIsConfirmDeleteOpen(false);

    deleteMonitoring(
      { id: params.id },
      {
        onSuccess: () => {
          navigate(`/monitoring`);
          notify("Monitoring Record removed", {
            position: "bottom-left",
            type: "success",
          });
        },
        onError: (err: any) => {
          showError(
            err,
            "A problem occurred while deleting the monitoring record.",
          );
        },
      },
    );
  };

  return (
    <div>
      <Topbar
        header={
          <Flex justifyContent="space-between" width="100%">
            <>
              <Breadcrumb>
                <Breadcrumb.Section linkTo={ROUTE.MONITORING}>
                  Monitoring
                </Breadcrumb.Section>

                <Breadcrumb.Section>{individualName}</Breadcrumb.Section>
              </Breadcrumb>
              <Flex justifyContent="space-between">
                {individual && (
                  <Box mr={1}>
                    <RecordSharing
                      module="monitoring"
                      recordId={individual?.id}
                      recordName={individualName}
                      recordReference={individual?.reference}
                      noun="monitoring record"
                      primaryDivision={individual?.primary_division}
                      primaryTeam={individual?.primary_team}
                      additionalDivisions={individual?.additional_divisions}
                      additionalTeams={individual?.additional_teams}
                      invalidateKeys={[["monitoringDetail", params.id]]}
                    />
                  </Box>
                )}
                {individual && (
                  <Button
                    onClick={handleDownloadPdfClick}
                    isDisabled={
                      isRequestingPdfDownload ||
                      !hasPermission("monitoring.downloadpdf")
                    }
                    title={
                      hasPermission("monitoring.downloadpdf")
                        ? undefined
                        : "You don't have permission to download this record as a PDF"
                    }
                    variant="secondary"
                    color={theme.colors.gray[80]}
                    mr={1}
                  >
                    <Image
                      src={downloadIcon}
                      alt="download pdf"
                      height="auto"
                      width="22px"
                      mr={1}
                    />
                    <Paragraph fontWeight="500" fontSize={theme.fontSizes[1]}>
                      {isRequestingPdfDownload
                        ? "Downloading..."
                        : "Download PDF"}
                    </Paragraph>
                  </Button>
                )}
                <IconButtonDropdown placement="bottom-end">
                  <IconButtonDropdownOption
                    onClick={
                      hasPermission("monitoring.delete")
                        ? () => setIsConfirmDeleteOpen(true)
                        : undefined
                    }
                    isDisabled={!hasPermission("monitoring.delete")}
                    title={
                      hasPermission("monitoring.delete")
                        ? undefined
                        : "You don't have permission to delete this monitoring record"
                    }
                    IconType={FaTrash}
                    isDestructive
                  >
                    Delete Monitoring Record
                  </IconButtonDropdownOption>
                </IconButtonDropdown>
              </Flex>
            </>
          </Flex>
        }
      ></Topbar>

      {isLoading && (
        <Flex justifyContent="center" alignItems="center" minHeight="60vh">
          <Loader />
        </Flex>
      )}

      {!isLoading && (
        <>
          <Container>
            {/* On a LexisNexis2 record the subject details sit across the top as
                a header band rather than in a left rail, so the two columns
                below get the width the match tables need. The Profile link is a
                relationship to another object, not one more field on this
                record, so it keeps its own card.

                Both cards are direct flex children so `stretch` gives them equal
                height on its own. Wrapping them in Boxes and setting height:100%
                on the cards resolved against a parent with no definite height,
                which is what pushed them over the content below. */}
            {isLn2 && (
              <Flex gap="24" alignItems="stretch" mb={4} flexWrap="wrap">
                <MonitoringRecordSummary
                  monitoring={individual}
                  isEntity={isEntity}
                  countryLabel={countryLabel}
                  countryFlag={countryFlag}
                  entityTypeName={entityTypeName}
                />
                <Card mb={0} p={3} width="300px" flexShrink={0}>
                  <ProfileRecordWidget
                    record={individual}
                    recordId={individual?.id}
                    recordType="monitoring"
                    subjectType={isEntity ? "entity" : "individual"}
                  />
                </Card>
              </Flex>
            )}

            <Flex gap="24">
              {/* not merely hidden on LexisNexis2: this card also mounts the
                  ProfileRecordWidget, which fetches and owns a slider, and two
                  live copies of it on one page is asking for trouble */}
              {!isLn2 && (
              <Box width={"20%"} flexShrink={0}>
                <H3 mb={2}>Details </H3>
                <Card mb={0}>
                  <Button
                    mb={3}
                    onClick={() =>
                      navigate(`/applications/new`, {
                        state: { checks: true, id: individual?.mandate },
                      })
                    }
                    mr={1}
                    variant="gray"
                    paddingLeft={2}
                    backgroundColor="gray.10"
                    border="2px solid background"
                    color="blue.primary"
                    fontWeight={600}
                    isDisabled={mandate?.checks_in_pending !== 0}
                    display={"none"}
                  >
                    <Image
                      src={resendInviteIcon}
                      alt="link application"
                      height="auto"
                      width="22px"
                      mr={1}
                    />
                    Link application
                  </Button>

                  <ProfileRecordWidget
                    record={individual}
                    recordId={individual?.id}
                    recordType="monitoring"
                    subjectType={isEntity ? "entity" : "individual"}
                  />
                  <Heading> Monitoring Reference</Heading>
                  <CardLine mb={3}>{individual?.reference}</CardLine>
                  <Heading>Your Reference</Heading>
                  <CardLine mb={3}>
                    {individual?.external_reference || "-"}
                  </CardLine>
                  <Heading>Date Started</Heading>
                  <CardLine mb={3}>
                    {individual?.created_on
                      ? DateTime.fromISO(individual?.created_on).toFormat(
                          "dd/MM/yyyy",
                        )
                      : "-"}
                  </CardLine>
                  <Heading>Last Activity</Heading>
                  <CardLine mb={3}>
                    {individual?.last_activity
                      ? DateTime.fromISO(individual?.last_activity).toFormat(
                          "dd/MM/yyyy",
                        )
                      : "-"}
                  </CardLine>
                  <Heading>Name</Heading>
                  <CardLine mb={3}>
                    {isIndividual
                      ? buildName([
                          individual?.first_name,
                          individual?.middle_name,
                          individual?.last_name,
                        ])
                      : individual?.company_name || "-"}
                  </CardLine>

                  {isIndividual && (
                    <>
                      <Heading>Born</Heading>
                      <CardLine mb={3}>
                        {individual?.date_of_birth
                          ? DateTime.fromISO(
                              individual?.date_of_birth,
                            ).toFormat("dd/MM/yyyy")
                          : "-"}
                      </CardLine>

                      <Heading>Gender</Heading>
                      <CardLine mb={3}>
                        {individual?.gender
                          ? capitalise(individual?.gender)
                          : "-"}
                      </CardLine>
                    </>
                  )}

                  {isEntity && (
                    <>
                      <Heading>Country of incorporation</Heading>

                      {individual?.country && countryLabel && countryFlag ? (
                        <Flex gap="6" alignItems="center" mt={1} mb={3}>
                          <img
                            src={`data:image/png;base64,${countryFlag}`}
                            alt="iamge"
                          />
                          <CardLine color={"gray.60"}>
                            {capitalise(countryLabel)}
                          </CardLine>
                        </Flex>
                      ) : (
                        "-"
                      )}

                      <Heading>Date of incorporation</Heading>
                      <CardLine mb={3}>
                        {individual?.date_of_incorporation
                          ? DateTime.fromISO(
                              individual?.date_of_incorporation,
                            ).toFormat("dd/MM/yyyy")
                          : "-"}
                      </CardLine>

                      <Heading>Type</Heading>
                      <CardLine mb={3}>
                        {individual?.entity_type ? entityTypeName : "-"}
                      </CardLine>

                      <Heading>Registration Number</Heading>
                      <CardLine mb={3}>
                        {individual?.registration_number
                          ? individual?.registration_number
                          : "-"}
                      </CardLine>

                      <Heading>Delete date</Heading>
                      <CardLine mb={3}>
                        {individual?.deleted_on
                          ? DateTime.fromISO(individual?.deleted_on).toFormat(
                              "dd/MM/yyyy",
                            )
                          : "-"}
                      </CardLine>

                      <Paragraph color="gray.60" mb={3}>
                        {individual?.deleted_on
                          ? "These screening will be deleted automatically on this date as part of our data retention policy."
                          : ""}
                      </Paragraph>
                    </>
                  )}
                </Card>
              </Box>
              )}
              <Box flex={1} minWidth={0}>
                {showScreeningView && (
                  <Box mb={showAboutCards ? 4 : 0}>
                    <MonitoringScreeningBody
                      monitoringId={params.id}
                      monitoring={individual}
                      activities={monitoringActivity ?? []}
                      legacyActivities={legacyActivities}
                      onSelectLegacyActivity={(activity: any) =>
                        handleViewClick(activity?.event, activity?.id)
                      }
                      isEntity={isEntity}
                      selection={timelineSelection}
                      onSelectTimelineEntry={setTimelineSelection}
                    />
                  </Box>
                )}

                {showAboutCards && (
                  <>
                <H3 mb={2}>About</H3>
                <Flex>
                  <Card mb={2} width={"100%"}>
                    <Flex gap="24">
                      <Box flex={1} borderColor="gray.20">
                        <Heading mb={2}>
                          {isIndividual ? "PEP & Sanctions" : "Sanctions"}
                        </Heading>

                        <Flex gap="24">
                          <Box flex={1}>
                            <CardLine>Required</CardLine>
                            <Heading>
                              {individual?.pep_and_sanction_required === true
                                ? "Yes"
                                : "No"}
                            </Heading>
                          </Box>
                          <Box flex={1}>
                            <CardLine>First Activity</CardLine>
                            <Heading>
                              {firstActivity !== undefined
                                ? DateTime.fromISO(firstActivity).toFormat(
                                    "dd/MM/yyyy",
                                  )
                                : "-"}
                            </Heading>
                          </Box>
                          <Box flex={1}>
                            <CardLine>Last Activity</CardLine>
                            <Heading>
                              {lastActivity !== undefined
                                ? DateTime.fromISO(lastActivity).toFormat(
                                    "dd/MM/yyyy",
                                  )
                                : "-"}
                            </Heading>
                          </Box>
                        </Flex>
                      </Box>
                    </Flex>
                  </Card>
                </Flex>
                <Flex>
                  <Card mb={2} width={"100%"}>
                    <Flex gap="24">
                      <Box flex={1}>
                        <Heading mb={2}>Adverse Media</Heading>

                        <Flex gap="24">
                          <Box flex={1}>
                            <CardLine>Required</CardLine>
                            <Heading>
                              {individual?.adverse_media_required === true
                                ? "Yes"
                                : "No"}
                            </Heading>
                          </Box>
                          <Box flex={1}>
                            <CardLine>First Activity</CardLine>
                            <Heading>
                              {firstActivityAdverse !== undefined
                                ? DateTime.fromISO(
                                    firstActivityAdverse,
                                  ).toFormat("dd/MM/yyyy")
                                : "-"}
                            </Heading>
                          </Box>
                          <Box flex={1}>
                            <CardLine>Last Activity</CardLine>
                            <Heading>
                              {lastActivityAdverse !== undefined
                                ? DateTime.fromISO(
                                    lastActivityAdverse,
                                  ).toFormat("dd/MM/yyyy")
                                : "-"}
                            </Heading>
                          </Box>
                        </Flex>
                      </Box>
                    </Flex>
                  </Card>
                </Flex>
                  </>
                )}

                {/* the timeline rail is the navigation on a LexisNexis2 record,
                    and it lists legacy activities too, so this table would be a
                    second copy of the same list */}
                {!showScreeningView && (
                  <>
                <H3 mb={2}>Activity</H3>
                <Flex>
                  {monitoringActivity?.length > 0 && (
                    <div className="tableContainer" style={{ width: "100%" }}>
                      <table style={{ margin: "0px" }}>
                        <thead>
                          {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup?.id}>
                              {headerGroup?.headers.map((header) => (
                                <th key={header?.id} colSpan={header?.colSpan}>
                                  {header?.isPlaceholder ? null : (
                                    <>
                                      <div
                                        {...{
                                          className: header?.column.getCanSort()
                                            ? "select-none cursor-pointer flex items-center gap-1"
                                            : "",
                                          onClick:
                                            header?.column.getToggleSortingHandler(),
                                        }}
                                      >
                                        {flexRender(
                                          header.column.columnDef.header,
                                          header.getContext(),
                                        )}
                                        {{
                                          asc: (
                                            <Icon
                                              display="inline-block"
                                              ml={1}
                                              size="20px"
                                              color="gray.40"
                                              Type={FaSortDown}
                                            ></Icon>
                                          ),
                                          desc: (
                                            <Icon
                                              display="inline-block"
                                              ml={1}
                                              size="20px"
                                              color="gray.40"
                                              Type={FaSortUp}
                                            ></Icon>
                                          ),
                                        }[
                                          header?.column.getIsSorted() as string
                                        ] ?? null}
                                      </div>
                                    </>
                                  )}
                                </th>
                              ))}
                            </tr>
                          ))}
                        </thead>
                        <tbody>
                          {table?.getRowModel().rows.map((row, index) => (
                            <tr key={row?.id}>
                              {row.getVisibleCells().map((cell) => (
                                <td key={cell?.id}>
                                  {flexRender(
                                    cell?.column.columnDef.cell,
                                    cell?.getContext(),
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <Flex
                        justifyContent="space-between"
                        alignItems="center"
                      ></Flex>
                    </div>
                  )}
                </Flex>
                  </>
                )}
              </Box>
              {checkContent && (
                <Box
                  width={"60%"}
                  z-index={"2"}
                  position={"absolute"}
                  pl={"30%"}
                >
                  <>
                    <Card p={"0px !important"}>{checkContent}</Card>
                  </>
                </Box>
              )}
            </Flex>
          </Container>
          <Modal
            isOpen={isConfirmDeleteOpen}
            onClose={() => setIsConfirmDeleteOpen(false)}
            onModalConfirmClick={handleConfirmDeleteMonitoringRecord}
            title="Do you want to delete this monitoring record?"
            content="This is an irreversible action, if you delete it you will not be able to undo the change. "
            hasSecondaryButton
            secondaryButtonText="Cancel"
            width="450px"
            primaryButtonText="Delete"
            primaryButtonVariant="danger"
          />
        </>
      )}

      {isPepAndSanctionsCheck && (
        <PepAndSanctionsMonitoring
          isOpen={isPepAndSanctionsCheck}
          isIndividual={isIndividual}
          onRequestClose={() => {
            refetchIndividual();
            setIsPepAndSanctionsCheck(false);
          }}
          id={monitoringId}
        />
      )}

      {isAdverseMediaCheck && (
        <AdverseMediaMonitoring
          isOpen={isAdverseMediaCheck}
          onRequestClose={() => {
            refetchIndividual();
            setIsAdverseMediaCheck(false);
          }}
          id={monitoringId}
        />
      )}
    </div>
  );
};
